import { useMemo } from 'react';

import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';

export const STRUCTURED_FILTER_HIDDEN_LEAF_IDS_STORAGE_KEY =
  'structured_filter_hidden_ids';

export type StructuredFilterVisibilityState = {
  hiddenCount: number;
  leafCount: number;
  visibleCount: number;
  isChecked: boolean;
  isIndeterminate: boolean;
};

export const parseHiddenLeafFilterIds = (value: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')
      ? parsed
      : [];
  } catch {
    return [];
  }
};

export const getStructuredFilterLeaves = (
  field: StructuredField,
): StructuredField[] => {
  const childEntries = Object.values(field.childField ?? {});
  if (childEntries.length === 0) return [field];
  return childEntries.flatMap(getStructuredFilterLeaves);
};

export const getStructuredFilterVisibilityState = (
  field: StructuredField,
  hiddenLeafIds: ReadonlySet<string>,
): StructuredFilterVisibilityState => {
  const leaves = getStructuredFilterLeaves(field);
  const hiddenCount = leaves.filter((leaf) =>
    hiddenLeafIds.has(leaf.columnKey),
  ).length;

  return {
    hiddenCount,
    leafCount: leaves.length,
    visibleCount: leaves.length - hiddenCount,
    isChecked: hiddenCount === 0,
    isIndeterminate: hiddenCount > 0 && hiddenCount < leaves.length,
  };
};

export const toggleStructuredFilterVisibility = (
  field: StructuredField,
  hiddenLeafIds: readonly string[],
  isVisible: boolean,
  allLeafIds: readonly string[],
) => {
  const nextHiddenLeafIds = new Set(hiddenLeafIds);

  getStructuredFilterLeaves(field).forEach((leaf) => {
    if (isVisible) {
      nextHiddenLeafIds.delete(leaf.columnKey);
    } else {
      nextHiddenLeafIds.add(leaf.columnKey);
    }
  });

  if (
    !isVisible &&
    allLeafIds.length > 0 &&
    allLeafIds.every((leafId) => nextHiddenLeafIds.has(leafId))
  ) {
    return [...hiddenLeafIds];
  }

  return [...nextHiddenLeafIds];
};

export const filterStructuredFieldByHiddenLeaves = (
  field: StructuredField,
  hiddenLeafIds: ReadonlySet<string>,
): StructuredField | null => {
  const childEntries = Object.entries(field.childField ?? {});
  if (childEntries.length === 0) {
    return hiddenLeafIds.has(field.columnKey) ? null : field;
  }

  const visibleChildren = Object.fromEntries(
    childEntries
      .map(([key, child]) => [
        key,
        filterStructuredFieldByHiddenLeaves(child, hiddenLeafIds),
      ])
      .filter((entry): entry is [string, StructuredField] => Boolean(entry[1])),
  );

  if (Object.keys(visibleChildren).length === 0) return null;
  return { ...field, childField: visibleChildren };
};

export const useStructuredFilterVisibility = () => {
  const [hiddenLeafIds, setHiddenLeafIds, clearHiddenLeafIds] =
    useWorkspaceStorage<string[]>(
      STRUCTURED_FILTER_HIDDEN_LEAF_IDS_STORAGE_KEY,
      [],
      { parser: parseHiddenLeafFilterIds },
    );

  const hiddenLeafIdSet = useMemo(
    () => new Set(hiddenLeafIds),
    [hiddenLeafIds],
  );

  return {
    hiddenLeafIds,
    hiddenLeafIdSet,
    setHiddenLeafIds,
    clearHiddenLeafIds,
  };
};
